import {colors} from '@ark/react';
import {lighten} from '@mui/material/styles';
import type {
  ChartColor,
  RampColor,
  RampTones,
} from '../components/charts/types';

export type RankPalette = {
  scale: RampTones;
  context: string;
  unranked: string;
  attention: string;
  reference: string;
};

export type ChartPalette = {
  series: Record<ChartColor, string>;
  heatmap: string[];
  ramps: Record<RampColor, RampTones>;
  rank: RankPalette;
  meta: string;
};

const scale: RampTones = [
  colors.rankScale1,
  colors.rankScale2,
  colors.rankScale3,
  colors.rankScale4,
];

const ramps: Record<RampColor, RampTones> = {
  blue: [
    colors.rankBlue1,
    colors.rankBlue2,
    colors.rankBlue3,
    colors.rankBlue4,
  ],
  green: [
    colors.rankGreen1,
    colors.rankGreen2,
    colors.rankGreen3,
    colors.rankGreen4,
  ],
  plum: [
    colors.rankPlum1,
    colors.rankPlum2,
    colors.rankPlum3,
    colors.rankPlum4,
  ],
  teal: [
    colors.rankTeal1,
    colors.rankTeal2,
    colors.rankTeal3,
    colors.rankTeal4,
  ],
  orange: [
    colors.rankOrange1,
    colors.rankOrange2,
    colors.rankOrange3,
    colors.rankOrange4,
  ],
  gold: [
    colors.rankGold1,
    colors.rankGold2,
    colors.rankGold3,
    colors.rankGold4,
  ],
  indigo: [
    colors.rankIndigo1,
    colors.rankIndigo2,
    colors.rankIndigo3,
    colors.rankIndigo4,
  ],
  magenta: [
    colors.rankMagenta1,
    colors.rankMagenta2,
    colors.rankMagenta3,
    colors.rankMagenta4,
  ],
};

const onDark = (tones: RampTones): RampTones => [
  lighten(tones[3], 0.5),
  tones[3],
  lighten(tones[2], 0.3),
  tones[2],
];

const light: ChartPalette = {
  series: {
    blue: colors.buttonBlue,
    orange: colors.jaffa,
    green: colors.jungleGreen,
    red: colors.carnation,
    purple: colors.violet,
    sky: colors.twitterBlue,
    yellow: colors.tulipTree,
    teal: colors.mosque,
    magenta: colors.rankMagenta2,
    brown: colors.seriesBrown,
  },
  heatmap: [
    colors.heatmapBlue1,
    colors.heatmapBlue2,
    colors.heatmapBlue3,
    colors.heatmapBlue4,
    colors.buttonBlue,
  ],
  ramps,
  meta: colors.rankMeta,
  rank: {
    scale,
    context: colors.rankContext,
    unranked: colors.rankUnranked,
    attention: colors.brick,
    reference: colors.rankReference,
  },
};

const dark: ChartPalette = {
  series: {
    blue: colors.cornflowerBlue,
    orange: colors.treePoppy,
    green: colors.caribbeanGreen,
    red: colors.carnation,
    purple: colors.lightViolet,
    sky: colors.pastelBlue,
    yellow: colors.saffron,
    teal: colors.pelorous,
    magenta: colors.rankMagenta3,
    brown: colors.seriesBrownDark,
  },
  heatmap: [
    colors.heatmapNavy1,
    colors.heatmapNavy2,
    colors.heatmapNavy3,
    colors.heatmapNavy4,
    colors.cornflowerBlue,
  ],
  ramps: {
    blue: onDark(ramps.blue),
    green: onDark(ramps.green),
    plum: onDark(ramps.plum),
    teal: onDark(ramps.teal),
    orange: onDark(ramps.orange),
    gold: onDark(ramps.gold),
    indigo: onDark(ramps.indigo),
    magenta: onDark(ramps.magenta),
  },
  meta: colors.rankMetaDark,
  rank: {
    scale: [
      colors.rankUnranked,
      colors.rankScale4,
      colors.rankScale3,
      colors.rankScale2,
    ],
    context: colors.rankScale2,
    unranked: colors.rankScale1,
    attention: colors.brick,
    reference: colors.rankUnranked,
  },
};

export const chartPalette = {light, dark};

export const chartTokens = {
  seriesOrder: [
    'blue',
    'orange',
    'green',
    'purple',
    'red',
    'sky',
    'yellow',
    'teal',
    'magenta',
    'brown',
  ] satisfies ChartColor[],
  height: 240,
  cardRadius: 10,
  lineWidth: 2,
  primaryLineWidth: 2.75,
  contextLineWidth: 1,
  contextOpacity: 0.35,
  trendWidth: 1.5,
  trendDash: '4 4',
  seriesDash: '6 4',
  trendOpacity: 0.75,
  areaOpacity: 0.28,
  barRadius: 4,
  barMaxSize: 36,
  barRowHeight: 30,
  donutThickness: 22,
  heatmapCellHeight: 22,
  heatmapCellGap: 2,
  heatmapLabelWidth: 120,
  valueAxisWidth: 44,
  labelMaxLength: 24,
  rankLabelWidth: 158,
  rankValueWidth: 54,
  rankBarHeight: 10,
  rankRowHeight: 14,
  rankRowGap: 7,
  sankeyNodeWidth: 16,
  sankeyNodePadding: 14,
  sparklinePoints: 24,
  animationDuration: 400,
  animationEasing: 'ease-out' as const,
};
